import { AxiosResponse } from 'axios';
import { apiClient } from '../instance';
import type {
  Paginated,
  PushApp,
  PushAudienceEstimate,
  PushCampaign,
  PushCampaignDraft,
  PushCampaignFilters,
  PushCampaignListItem,
  PushDeliveryErrorRow,
  PushImage,
  PushLanguage,
  PushNotificationType,
  PushReport,
  PushTemplate,
  PushTestDevice,
  PushValidationResult,
} from '../../types/marketingPushInterfaces';

const BASE = '/marketing';

/**
 * LimitOffsetPagination в ядре без PAGE_SIZE: конверт {count, results}
 * приходит только при ?limit=, иначе — голый массив. Нормализуем оба
 * варианта, как в diagnostics.service.
 */
export function asList<T>(data: Paginated<T> | T[]): T[] {
  return Array.isArray(data) ? data : data?.results ?? [];
}

/** Общее число записей. У голого массива — его длина. */
export function asCount<T>(data: Paginated<T> | T[]): number {
  return Array.isArray(data) ? data.length : data?.count ?? 0;
}

/** Убирает пустые значения; массивы разворачиваются в повторяющиеся ключи. */
function listParams(filters: PushCampaignFilters): URLSearchParams {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '' || value === false) return;
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, String(item)));
    } else if (value === true) {
      params.append(key, '1');
    } else {
      params.append(key, String(value));
    }
  });
  return params;
}

export const marketingPushApi = {
  /* ── справочники ── */
  getApps: (): Promise<AxiosResponse<PushApp[]>> =>
    apiClient.get<PushApp[]>(`${BASE}/apps/`),

  getLanguages: (): Promise<AxiosResponse<PushLanguage[]>> =>
    apiClient.get<PushLanguage[]>(`${BASE}/languages/`),

  getNotificationTypes: (): Promise<AxiosResponse<PushNotificationType[]>> =>
    apiClient.get<PushNotificationType[]>(`${BASE}/notification-types/`),

  saveNotificationType: (
    data: Partial<PushNotificationType>,
  ): Promise<AxiosResponse<PushNotificationType>> =>
    data.id
      ? apiClient.patch<PushNotificationType>(`${BASE}/notification-types/${data.id}/`, data)
      : apiClient.post<PushNotificationType>(`${BASE}/notification-types/`, data),

  /* ── рассылки ── */
  getCampaigns: (
    filters: PushCampaignFilters = {},
  ): Promise<AxiosResponse<Paginated<PushCampaignListItem>>> =>
    apiClient.get<Paginated<PushCampaignListItem>>(`${BASE}/campaigns/`, {
      params: listParams(filters),
    }),

  getCampaignById: (id: number): Promise<AxiosResponse<PushCampaign>> =>
    apiClient.get<PushCampaign>(`${BASE}/campaigns/${id}/`),

  createCampaign: (data: PushCampaignDraft): Promise<AxiosResponse<PushCampaign>> =>
    apiClient.post<PushCampaign>(`${BASE}/campaigns/`, data),

  updateCampaign: (
    id: number,
    data: PushCampaignDraft,
  ): Promise<AxiosResponse<PushCampaign>> =>
    apiClient.put<PushCampaign>(`${BASE}/campaigns/${id}/`, data),

  /** Мягкое удаление: уведомление у пользователей остаётся. */
  deleteCampaign: (id: number): Promise<AxiosResponse<void>> =>
    apiClient.delete<void>(`${BASE}/campaigns/${id}/`),

  validateCampaign: (id: number): Promise<AxiosResponse<PushValidationResult>> =>
    apiClient.get<PushValidationResult>(`${BASE}/campaigns/${id}/validate/`),

  getAudienceEstimate: (id: number): Promise<AxiosResponse<PushAudienceEstimate>> =>
    apiClient.get<PushAudienceEstimate>(`${BASE}/campaigns/${id}/audience-estimate/`),

  /**
   * Запуск рассылки.
   *
   * Ключ идемпотентности обязателен и должен быть одним и тем же между
   * попытками: если ответ потерялся в сети, повтор с новым ключом создал бы
   * вторую отправку. Блокировка кнопки от этого не спасает.
   */
  sendCampaign: (
    id: number,
    idempotencyKey: string,
  ): Promise<AxiosResponse<{ status: string; idempotency_key: string }>> =>
    apiClient.post(`${BASE}/campaigns/${id}/send/`, null, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }),

  scheduleCampaign: (
    id: number,
    scheduledAt: string,
    timezone: string,
  ): Promise<AxiosResponse<{ status: string; scheduled_at: string }>> =>
    apiClient.post(`${BASE}/campaigns/${id}/schedule/`, {
      scheduled_at: scheduledAt,
      timezone,
    }),

  cancelCampaign: (id: number): Promise<AxiosResponse<{ status: string }>> =>
    apiClient.post(`${BASE}/campaigns/${id}/cancel/`),

  /** Тест уходит на привязанные устройства сотрудника. Пустой список —
   *  на все подходящие под выбранные приложения. */
  testCampaign: (
    id: number,
    deviceIds: number[] = [],
  ): Promise<AxiosResponse<{ test_campaign_id: number; recipients: number }>> =>
    apiClient.post(`${BASE}/campaigns/${id}/test/`, { device_ids: deviceIds }),

  duplicateCampaign: (id: number): Promise<AxiosResponse<PushCampaign>> =>
    apiClient.post<PushCampaign>(`${BASE}/campaigns/${id}/duplicate/`),

  /* ── отчётность ── */
  getReport: (id: number): Promise<AxiosResponse<PushReport>> =>
    apiClient.get<PushReport>(`${BASE}/campaigns/${id}/report/`),

  getReportErrors: (
    id: number,
    params: { limit?: number; offset?: number } = {},
  ): Promise<AxiosResponse<Paginated<PushDeliveryErrorRow>>> =>
    apiClient.get<Paginated<PushDeliveryErrorRow>>(`${BASE}/campaigns/${id}/report/errors/`, {
      params: { limit: 100, ...params },
    }),

  /* ── шаблоны ── */
  getTemplates: (): Promise<AxiosResponse<Paginated<PushTemplate>>> =>
    apiClient.get<Paginated<PushTemplate>>(`${BASE}/templates/`, { params: { limit: 200 } }),

  saveTemplate: (
    data: Partial<PushTemplate> & { id?: number },
  ): Promise<AxiosResponse<PushTemplate>> =>
    data.id
      ? apiClient.put<PushTemplate>(`${BASE}/templates/${data.id}/`, data)
      : apiClient.post<PushTemplate>(`${BASE}/templates/`, data),

  deleteTemplate: (id: number): Promise<AxiosResponse<void>> =>
    apiClient.delete<void>(`${BASE}/templates/${id}/`),

  /* ── изображения ── */
  uploadImage: (file: File): Promise<AxiosResponse<PushImage>> => {
    const form = new FormData();
    form.append('file', file);
    // Content-Type не задаём: браузер сам подставит boundary, а заголовок,
    // выставленный руками, его затрёт и запрос развалится.
    return apiClient.post<PushImage>(`${BASE}/media/`, form, {
      headers: { 'Content-Type': undefined as unknown as string },
    });
  },

  getImages: (
    params: { limit?: number; offset?: number } = {},
  ): Promise<AxiosResponse<Paginated<PushImage>>> =>
    apiClient.get<Paginated<PushImage>>(`${BASE}/media/`, {
      params: { limit: 50, ...params },
    }),

  /* ── тестовые устройства ── */
  getTestDevices: (): Promise<AxiosResponse<PushTestDevice[]>> =>
    apiClient.get<PushTestDevice[]>(`${BASE}/test-devices/`),

  /** Привязка по короткому коду из приложения. install_id наружу не
   *  отдаётся: по нему читается история уведомлений установки. */
  pairTestDevice: (
    code: string,
    label?: string,
  ): Promise<AxiosResponse<PushTestDevice>> =>
    apiClient.post<PushTestDevice>(`${BASE}/test-devices/pair/`, { code, label }),

  unpairTestDevice: (id: number): Promise<AxiosResponse<void>> =>
    apiClient.delete<void>(`${BASE}/test-devices/${id}/`),
};

/**
 * Человекочитаемая причина из ответа DRF.
 *
 * Ядро отдаёт ошибки в нескольких формах: {detail}, {field: [...]},
 * и {problems: [...]} у наших действий. Разбирать это в каждом компоненте
 * значило бы показывать пользователю то «[object Object]», то пустоту.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function pushError(error: any, fallback = 'Не удалось выполнить действие'): string {
  const data = error?.response?.data;
  if (!data) return error?.message || fallback;
  if (typeof data === 'string') return data;
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.problems)) return data.problems.join(' ');

  const parts: string[] = [];
  Object.entries(data).forEach(([field, value]) => {
    const text = Array.isArray(value) ? value.join(' ') : String(value);
    parts.push(field === 'non_field_errors' ? text : `${field}: ${text}`);
  });
  return parts.join('\n') || fallback;
}
